import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { inspectionService } from "../../services/inspection.service";
import axiosInstance from "../../services/axios.config";
import { publishMissionPreview, CompiledMission } from "../../services/mission-preview";

import { inventoryService, InventoryType } from "../../services/inventory.service";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";

export default function InspectionModal({ isOpen, onClose, onInspectionCreated }: { isOpen: boolean; onClose: () => void; onInspectionCreated?: () => void; }): React.ReactElement {
    const { user } = useSelector((e: RootState) => ({ user: e.user }))
    const [trailerId, setTrailerId] = useState<string>("");
    const [inventory, setInventory] = useState<InventoryType[]>([]);
    const [showTrailerSuggestions, setShowTrailerSuggestions] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [inspectionId, setInspectionId] = useState<number | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        const loadInventory = async () => {
            try {
                const data = await inventoryService.get();
                setInventory(data);
            } catch (err) {
                console.error("Failed to load inventory for trailer autocomplete:", err);
                setInventory([]);
            }
        };

        loadInventory();
    }, [isOpen]);

    const trailerSuggestions = Array.from(
        new Map(
            inventory
                .filter((item) =>
                    item.status === "Entered" &&
                    item.trailerId &&
                    item.trailerId !== "Unidentified" &&
                    item.trailerId.toLowerCase().includes(trailerId.trim().toLowerCase())
                )
                .map((item) => [item.trailerId, item])
        ).values()
    ).slice(0, 10);

    const checklistItems = [
        "DOT Reflective Tape",
        "King Pin",
        "Landing Gear",
        "Tandem Slider/Pins",
        "Tires/Rims/Lug Nuts",
        "Valve Stems",
        "Mud Flaps",
        "Brake Shoes/Drums/Hoses",
        "Torque Rod",
        "Bumper",
        "Door/Door Locks",
        "Intermodal Locking Pins",
        "Structure/Holes/Puncture",
    ];

    const [checkedItems, setCheckedItems] = useState<boolean[]>(new Array(checklistItems.length).fill(false));
    const [fullChecked, setFullChecked] = useState<boolean>(false);

    const handleIndividualCheck = (index: number) => {
        const updated = [...checkedItems];
        updated[index] = !updated[index];
        setCheckedItems(updated);
        setFullChecked(updated.every(Boolean));
    };

    const clearAll = () => {
        setCheckedItems(new Array(checklistItems.length).fill(false));
        setFullChecked(false);
    };

    const generateInspectionPreview = async (
        trailerId: string,
        inspectionItems: string[]
    ): Promise<void> => {
        const response = await axiosInstance.post(
            '/yards/inspection/generate',
            {
                trailer_id: trailerId.trim(),
                inspection_items: inspectionItems
            }
        );

        if (response.data?.success !== true || !response.data?.mission) {
            throw new Error(
                response.data?.error || 'Inspection route generation failed'
            );
        }

        const compiled = await axiosInstance.post<CompiledMission>(
            '/yards/mission/compile',
            { mission: response.data.mission }
        );

        if (compiled.data?.success !== true) {
            throw new Error('Inspection mission validation failed');
        }

        publishMissionPreview(compiled.data);
    };

    const handleSubmit = async () => {
        if (!trailerId.trim()) {
            setError("Please enter a Trailer ID");
            return;
        }

        if (!user?.id) {
            setError("User not authenticated");
            return;
        }

        try {
            setLoading(true);
            setError("");

            // Prepare checklist data
            const selectedChecklistData: string[] = [];
            checklistItems.map((item, index) => {
                if (checkedItems[index]) {
                    selectedChecklistData.push(item);
                }
            });

            // Generate and display the route preview only.
            // Do not start or dispatch an Inspection job here.
            await generateInspectionPreview(
                trailerId,
                selectedChecklistData
            );

            onClose();
        } catch (err: any) {
            const errorData = err.response?.data;
            let errorMessage = errorData?.error || "Failed to submit inspection";

            // Add more context for existing inspection errors
            if (errorData?.existingStatus) {
                if (errorData.existingStatus === 'failed') {
                    errorMessage += ". Please resolve the failed inspection before starting a new one.";
                } else if (errorData.existingStatus === 'in-progress') {
                    errorMessage += ". Please complete or cancel the current inspection first.";
                }
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };



    if (!isOpen) return <></>;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center w-full p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-3">
                    <h2 className="text-black-1 text-xl font-[Geist-Bold]">Start Inspection</h2>
                    <button 
                        onClick={onClose} 
                        className="w-8 h-8 rounded-full bg-gray-1 hover:bg-gray-2 flex items-center justify-center transition-colors"
                    >
                        <Icon icon="material-symbols:close" className="text-gray-2 text-lg" />
                    </button>
                </div>
                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Trailer ID Input */}
                    <div className="mb-6">
                        <label className="block mb-3 text-black-1 text-sm font-[Geist-Medium]">Enter Trailer ID</label>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Enter Trailer ID here"
                                value={trailerId}
                                autoComplete="off"
                                onFocus={() => setShowTrailerSuggestions(true)}
                                onChange={(e) => {
                                    setTrailerId(e.target.value);
                                    setShowTrailerSuggestions(true);
                                }}
                                className="w-full px-4 py-3 border border-gray-3 rounded-lg text-black-1 text-sm font-[Geist-Medium] focus:outline-none focus:border-green transition-colors"
                            />

                            {showTrailerSuggestions && trailerId.trim() && trailerSuggestions.length > 0 && (
                                <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-lg border border-gray-3 bg-white shadow-lg">
                                    {trailerSuggestions.map((item) => (
                                        <button
                                            key={item.id}
                                            type="button"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                setTrailerId(item.trailerId);
                                                setShowTrailerSuggestions(false);
                                                setError("");
                                            }}
                                            className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-[Geist-Medium] text-black-1 hover:bg-gray-6 transition-colors"
                                        >
                                            <span>{item.trailerId}</span>
                                            <span className="text-xs text-gray-2">In Yard</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {error && (
                            <div className="mt-2 flex items-center gap-2 text-red-500 text-sm">
                                <Icon icon="material-symbols:error-outline" className="text-sm" />
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Checklist Section */}
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-black-1 text-sm font-[Geist-Medium]">Specify Action</h3>
                            {checkedItems.some(Boolean) && (
                                <div className="flex items-center gap-2 text-green text-sm font-[Geist-Medium]">
                                    <span>Selected ({checkedItems.filter(Boolean).length})</span>
                                    <button 
                                        onClick={clearAll} 
                                        className="text-gray-2 hover:text-red-500 transition-colors text-sm font-[Geist-Medium]"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Checklist Grid */}
                        <div className="bg-gray-6 border border-gray-3 rounded-xl p-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[360px] overflow-y-auto pr-2">
                                {checklistItems.map((item, index) => (
                                    <label 
                                        key={index} 
                                        className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                                            checkedItems[index] 
                                                ? 'bg-green-1 border border-green-200' 
                                                : 'bg-white border border-gray-3 hover:bg-gray-1'
                                        }`}
                                    >
                                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center mr-3 transition-colors ${
                                            checkedItems[index] 
                                                ? 'border-green bg-green' 
                                                : 'border-gray-3'
                                        }`}>
                                            {checkedItems[index] && (
                                                <Icon icon="material-symbols:check" className="text-white text-sm" />
                                            )}
                                        </div>
                                        <span className={`text-sm font-[Geist-Medium] ${
                                            checkedItems[index] ? 'text-green' : 'text-black-1'
                                        }`}>
                                            {item}
                                        </span>
                                        <input
                                            type="checkbox"
                                            checked={checkedItems[index]}
                                            onChange={() => handleIndividualCheck(index)}
                                            className="hidden"
                                        />
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-3 bg-gray-6">
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 text-gray-2 hover:text-black-1 text-sm font-[Geist-Medium] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="px-6 py-2 bg-blue hover:bg-blue-1 text-white rounded-lg text-sm font-[Geist-Medium] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loading && <Icon icon="eos-icons:loading" className="text-sm animate-spin" />}
                            {loading ? "Generating Preview..." : "Generate Flight Preview"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};